import { useEffect } from 'react';
import type { RefObject } from 'react';

/* --------------------------------------------------------------------------
   Focus the first field when an add / edit dialog opens
   --------------------------------------------------------------------------
   Testers asked for it ("New Allergy Group" opened with nothing focused, so
   the first thing every user did was click into Group Name). WinForms put the
   focus on the first control of every Add/Mod form; this does the same.

   DialogActionsBar calls this for every dialog that can save, so the add/edit
   dialogs get it without each one wiring its own autoFocus.

   The rules:
   - Only inside a dialog (role="dialog"/"alertdialog"). The same bar used as a
     page form's footer never moves the focus on page load.
   - The first text entry field (text box, number, date, text area) or plain
     dropdown in the dialog, in page order. Checkboxes, radios and buttons are
     passed over; so are disabled, read-only and hidden fields, and anything in
     a grid, list or tree.
   - A field marked `data-no-autofocus` (or inside such an element), or one
     whose list is already open, stops the search: nothing is focused. Use it on
     a picker that opens a popup as soon as it gets the focus.
   - A field (or button) that already took the focus with its own autoFocus
     keeps it. So does anything the user clicks or tabs to.
   - The caret goes to the END of any text already there. Nothing is
     selected, so a stray key in an Edit dialog adds a character rather than
     replacing the value.
   - Touch-only screens are left alone: focusing a field there pops the
     on-screen keyboard over the dialog.
   - A dialog whose fields arrive after it opens (loaded from the API) gets the
     focus when they appear, for a short while after opening.
   -------------------------------------------------------------------------- */

/** Everything that could take typing, in page order. */
const CANDIDATES = [
  'input',
  'textarea',
  'select',
  '[role="combobox"]',
  '[contenteditable=""]',
  '[contenteditable="true"]',
].join(',');

/** Input types that are not text entry: passed over, never focused. */
const SKIP_TYPES = new Set([
  'hidden', 'checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'image', 'range', 'color',
]);

/** Containers whose inputs are not dialog fields (grid cell editors, lists, trees, menus). */
const SKIP_CONTAINERS = [
  '[role="grid"]',
  '[role="treegrid"]',
  '[role="tree"]',
  '[role="listbox"]',
  '[role="menu"]',
  '[aria-hidden="true"]',
  '[hidden]',
  '[inert]',
  '[data-autofocus-skip]',
].join(',');

/** Text-entry types whose caret position can be set (number/email/date throw). */
const CARET_TYPES = new Set(['', 'text', 'search', 'url', 'tel', 'password']);

/** How long after opening a late-arriving field still gets the focus. */
const SETTLE_MS = 1500;
const POLL_MS = 50;

function isHiddenByStyle(el: HTMLElement, stopAt: HTMLElement): boolean {
  const view = el.ownerDocument.defaultView;
  if (!view) return false;
  for (let node: HTMLElement | null = el; node && node !== stopAt.parentElement; node = node.parentElement) {
    const style = view.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return true;
  }
  return false;
}

/**
 * The field to focus in `dialog`, or:
 * - `null` when there is none (yet): the dialog may still be loading;
 * - `false` when the first field must not be focused (`data-no-autofocus`, list open).
 * Anything inside `exclude` (the dialog's own button bar) is ignored.
 */
export function findFirstField(dialog: HTMLElement, exclude?: HTMLElement | null): HTMLElement | null | false {
  const all = dialog.querySelectorAll<HTMLElement>(CANDIDATES);
  for (const el of Array.from(all)) {
    if (exclude && exclude.contains(el)) continue;
    if (el instanceof HTMLInputElement && SKIP_TYPES.has(el.type)) continue;
    // A combobox's own input is found as the input; its wrapper role may sit on an ancestor.
    if (el.closest(SKIP_CONTAINERS)) continue;
    if ((el as HTMLInputElement).disabled || el.getAttribute('aria-disabled') === 'true') continue;
    if (el.getAttribute('aria-readonly') === 'true' || el.closest('.app-field--readonly')) continue;
    // A no-typing picker (SelectField searchEnabled={false}) is a read-only input with
    // role="combobox"; it still takes the focus and the arrow keys. A read-only text box
    // does not.
    if ((el as HTMLInputElement).readOnly && el.getAttribute('role') !== 'combobox') continue;
    if (el.tabIndex < 0) continue;
    if (isHiddenByStyle(el, dialog)) continue;
    // This is the first real field. Focus it, unless it must not be.
    if (el.closest('[data-no-autofocus]') || el.getAttribute('aria-expanded') === 'true') return false;
    return el;
  }
  return null;
}

/** Focus `el` with the caret after its text: never a selection a keystroke would replace. */
export function focusFieldCaretAtEnd(el: HTMLElement): void {
  el.focus();
  if (el instanceof HTMLTextAreaElement || (el instanceof HTMLInputElement && CARET_TYPES.has(el.type))) {
    const end = el.value.length;
    try {
      el.setSelectionRange(end, end);
    } catch {
      // Some input types refuse a selection; the focus alone is fine.
    }
  }
}

/** A touch-only screen (no mouse): focusing a field would raise the on-screen keyboard. */
function isTouchOnly(): boolean {
  try {
    return typeof window.matchMedia === 'function'
      && window.matchMedia('(hover: none) and (pointer: coarse)').matches === true;
  } catch {
    return false;
  }
}

/**
 * Focus the first field of the dialog `anchorRef` sits in, once, when it mounts.
 * `anchorRef` is any element inside the dialog (DialogActionsBar passes its own bar,
 * which is also left out of the search).
 */
export function useDialogAutoFocus(anchorRef: RefObject<HTMLElement | null>, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    const anchor = anchorRef.current;
    if (!anchor) return;
    const dialog = anchor.closest<HTMLElement>('[role="dialog"],[role="alertdialog"]');
    if (!dialog || isTouchOnly()) return;

    const doc = dialog.ownerDocument;
    // What had the focus as the dialog opened (usually the New / Modify button behind
    // it). A field inside the dialog that took the focus with its own autoFocus is not
    // an opener: it keeps the focus.
    const opener = dialog.contains(doc.activeElement) ? null : doc.activeElement;
    let ours: HTMLElement | null = null;
    let stopped = false;

    /** Nobody has chosen a field yet: the focus is still on the page, the dialog shell, or nowhere. */
    const idle = (active: Element | null): boolean =>
      !active
      || active === doc.body
      || active === doc.documentElement
      || active === dialog
      || active.contains(dialog) // MUI's focus-trap container around the dialog
      || active === opener;

    const attempt = () => {
      if (stopped) return;
      const active = doc.activeElement;
      if (ours && active === ours) return; // still ours; keep watching in case it is taken away
      if (!idle(active)) {
        stopped = true; // someone else's field, or the user's choice: leave it
        return;
      }
      if (!dialog.isConnected) {
        stopped = true;
        return;
      }
      const target = ours && ours.isConnected ? ours : findFirstField(dialog, anchor);
      if (target === false) {
        stopped = true;
        return;
      }
      if (target === null) return; // nothing to focus yet: the fields may still be loading
      ours = target;
      focusFieldCaretAtEnd(target);
    };

    // Now, before MUI's focus trap moves the focus to the dialog shell (its effect runs
    // after ours and leaves the focus alone when it is already inside the dialog), then
    // for a short while in case the fields render late or the focus is pulled back out.
    attempt();
    const started = Date.now();
    const timer = window.setInterval(() => {
      attempt();
      if (stopped || Date.now() - started > SETTLE_MS) window.clearInterval(timer);
    }, POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
    // Runs when the bar mounts (the dialog's content mounts when it opens).
  }, [anchorRef, enabled]);
}
